import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { FilterGroup, FilterGroupType, Filter, RuleOperator } from 'src/app/modules/core/store/models/filters.model';
import {
  QuickFilterTextOptions,
  QuickFilterPicklistOptions,
  QuickFilterNumberOptions,
  QuickFilterSelection,
  QuickFilterConfiguration
} from 'src/app/modules/cop/store/cop/models/quick-filter.model';
import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { CalcField, CalcFieldOperator } from 'src/app/modules/core/store/models/query.model';

export class QuickFilterHelperService {
  static addQuickFiltersIfActive(filterGroup: FilterGroup, layer: Layer, dataset: Dataset, quickFilterSelection: QuickFilterSelection): FilterGroup {
    const hasQuickFilterConfig = layer?.displayOptions?.quickFilter?.length > 0;
    if (!hasQuickFilterConfig || !quickFilterSelection || !dataset) {
      return filterGroup;
    }

    let calcFields: CalcField[] = [];

    const quickFilterConfig: QuickFilterConfiguration[] = layer.displayOptions.quickFilter;

    const quickFilterGroups = this.getQuickFilterGroups(dataset, quickFilterConfig, quickFilterSelection, calcFields);

    let encapsulatedFilterGroup = {
      groupType: FilterGroupType.And,
      filters: [],
      filterGroups: []
    };

    if (!!filterGroup) {
      encapsulatedFilterGroup.filterGroups = [filterGroup]
    }

    if (quickFilterGroups.length > 0) {
      encapsulatedFilterGroup.filterGroups.push(...quickFilterGroups);
    }

    return encapsulatedFilterGroup;
  }

  static getQuickFilterGroups(
    dataset: Dataset,
    quickFilterConfig: QuickFilterConfiguration[],
    filterSelections: { [filterUId: string]: { selectedValues: any[] } },
    calcFields: CalcField[]
  ): FilterGroup[] {
    let quickFilterCalFieldCount = 0;
    const quickFilterGroups: FilterGroup[] = [];

    quickFilterConfig.forEach(filterConfig => {
      const field = dataset?.schema?.fields?.find(f => f.uId === filterConfig.schemaFieldUId);
      if (field) {
        const fieldFilterGroup = {
          filters: [],
          filterGroups: [],
          groupType: FilterGroupType.Or
        };

        if (filterConfig.filterOptions?.type === 'date' && filterConfig.filterOptions?.operator === 'equal') {
          const datePart = filterConfig.filterOptions?.resolution;
          quickFilterCalFieldCount++;
          const calcFieldName = `quickfilter_${quickFilterCalFieldCount}_${field.systemName}`;
          const arrayYearSelections = filterSelections[filterConfig.uId]?.selectedValues?.slice() || [];

          arrayYearSelections.sort((b, a) => parseInt(a) - parseInt(b));

          arrayYearSelections.forEach(v => {
            fieldFilterGroup.filters.push({
              field: calcFieldName,
              operator: RuleOperator.Equal,
              value: (v || '').toString()
            } as Filter);
          });
          if (fieldFilterGroup.filters.length > 0) {
            calcFields.push({
              name: calcFieldName,
              calc: {
                operator: CalcFieldOperator.DatePart,
                expressions: [field.systemName, datePart]
              }
            });
          }
        }
        if (filterConfig.filterOptions?.type === 'number') {
          const textOptions = filterConfig.filterOptions as QuickFilterNumberOptions;
          const valuesForFilter = filterSelections[filterConfig.uId]?.selectedValues?.slice() || [];
          if (textOptions?.operator === 'range' && valuesForFilter.length > 0) {
            valuesForFilter.forEach((v, i) => {
              if (!isNaN(Number.parseFloat(v))) {
                fieldFilterGroup.filters.push({
                  field: field.systemName,
                  operator: i === 0 ? RuleOperator.GreaterThanEqualTo : RuleOperator.LessThanEqualTo,
                  value: v
                } as Filter);
              }
            });
            fieldFilterGroup.groupType = FilterGroupType.And;
          }
          if (textOptions?.operator === 'equal' && valuesForFilter.length > 0) {
            if (valuesForFilter.length < 2) {
              fieldFilterGroup.filters.push({
                field: field.systemName,
                operator: valuesForFilter[0] === null || valuesForFilter[0] === undefined ? RuleOperator.IsEmpty : RuleOperator.Equal,
                value: valuesForFilter[0]
              } as Filter);
            }
          }
        }
        if (filterConfig.filterOptions?.type === 'picklist') {
          const textOptions = filterConfig.filterOptions as QuickFilterPicklistOptions;
          const valuesForFilter = filterSelections[filterConfig.uId]?.selectedValues?.slice() || [];
          valuesForFilter.forEach(v => {
            fieldFilterGroup.filters.push({
              field: field.systemName,
              operator: textOptions?.operator === 'equal' ? RuleOperator.Contains : RuleOperator.DoesNotContain,
              value: v
            } as Filter);
          });
        }
        if (filterConfig.filterOptions?.type === 'text') {
          const textOptions = filterConfig.filterOptions as QuickFilterTextOptions;
          const valuesForFilter = filterSelections[filterConfig.uId]?.selectedValues?.slice() || [];
          valuesForFilter.forEach(v => {
            let textOperator: RuleOperator;
            switch (textOptions.operator) {
              case 'contains':
                textOperator = RuleOperator.Contains;
                break;
              case 'equal':
                textOperator = RuleOperator.Equal;
                break;
              case 'does-not-contain':
                textOperator = RuleOperator.DoesNotContain;
                break;
            }
            fieldFilterGroup.filters.push({
              field: field.systemName,
              operator: textOperator,
              value: v
            } as Filter);
          });
        }
        if (fieldFilterGroup.filters.length > 0 || fieldFilterGroup.filterGroups.length > 0) {
          quickFilterGroups.push(fieldFilterGroup);
        }
      }
    });

    return quickFilterGroups;
  }
}
